import React, { useCallback, useEffect, useRef, useState } from "react";
import { Camera, ImageIcon, Trash2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, API, spaceImageUrl, formatApiError } from "@/lib/api";

function SpaceRow({ space, onChanged }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const gallery = space.gallery || [];

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      await api.post(`/admin/spaces/${space.id}/images`, fd);
      toast.success(`Foto adicionada em ${space.name}!`);
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const removeImg = async (imageId) => {
    try {
      await api.delete(`/admin/space-images/${imageId}`);
      toast.success("Foto removida.");
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div
      data-testid={`space-image-row-${space.id}`}
      className="rounded-xl border border-slate-200 p-3"
    >
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-semibold text-slate-800">{space.name}</p>
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          onChange={onFile}
          className="hidden"
          data-testid={`space-file-input-${space.id}`}
        />
        <Button
          data-testid={`space-upload-btn-${space.id}`}
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          size="sm"
          variant="outline"
          className="rounded-full border-[#1B7A45] text-[#1B7A45] hover:bg-[#EAF5EE] disabled:opacity-50"
        >
          <Plus className="mr-1.5 h-4 w-4" />
          {uploading ? "Enviando..." : "Adicionar foto"}
        </Button>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        {gallery.length === 0 ? (
          <div className="relative">
            <img
              src={spaceImageUrl(space)}
              alt={space.name}
              className="h-20 w-28 rounded-lg object-cover opacity-70"
            />
            <span className="absolute bottom-1 left-1 rounded bg-black/60 px-1.5 py-0.5 text-[10px] text-white">
              padrão
            </span>
          </div>
        ) : (
          gallery.map((id) => (
            <div key={id} className="relative" data-testid={`space-photo-${id}`}>
              <img
                src={`${API}/space-images/${id}`}
                alt={space.name}
                className="h-20 w-28 rounded-lg object-cover"
              />
              <button
                data-testid={`space-photo-delete-${id}`}
                onClick={() => removeImg(id)}
                className="absolute -right-1.5 -top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-red-600 text-white shadow hover:bg-red-700"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default function SpaceImageManager() {
  const [spaces, setSpaces] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/spaces");
      setSpaces(res.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="mt-8" data-testid="space-image-manager">
      <div className="flex items-center gap-2">
        <ImageIcon className="h-5 w-5 text-[#1B7A45]" />
        <h2 className="font-display text-xl font-bold tracking-tight text-slate-900">
          Fotos das quadras e churrasqueira
        </h2>
      </div>
      <p className="mt-1 text-sm text-slate-500">
        Adicione várias fotos por espaço (galeria que passa sozinha no card). Recomendado: 1200×800px.
      </p>

      <div className="mt-4 space-y-3 rounded-2xl border border-slate-200 bg-white p-4">
        {loading ? (
          <div className="h-20 animate-pulse rounded-xl bg-slate-100" />
        ) : (
          spaces.map((s) => <SpaceRow key={s.id} space={s} onChanged={load} />)
        )}
      </div>
    </div>
  );
}
